// Vision Board — the player pins up to 6 dreams. Each is greyscale with a dashed
// border until its condition comes true, then it bursts to full colour (handled
// by VisionWatcher). Putting it up is step one; doing the work is the rest.

import { motion } from 'framer-motion';
import { useLife, useLifeComputed, useLifeDispatch } from './store.js';
import { Card, Eyebrow, Button, Chip } from '../ui/kit.js';
import { CATEGORIES, GALLERY, conditionLabel, conditionProgress, type GalleryItem } from './visionGallery.js';

const MAX = 6;

export function VisionBoardScreen({ onClose }: { onClose: () => void }) {
  const s = useLife();
  const c = useLifeComputed();
  const dispatch = useLifeDispatch();

  const board = s.visionBoard;
  const onBoard = (key: string) => board.some((v) => v.id === key);
  const achievedCount = board.filter((v) => v.achieved).length;
  const full = board.length >= MAX;

  const add = (g: GalleryItem) => {
    if (full || onBoard(g.key)) return;
    dispatch({
      type: 'ADD_VISION',
      item: { id: g.key, title: g.title, icon: g.icon, category: g.category, condition: g.condition, achieved: false },
    });
  };

  const done = () => {
    dispatch({ type: 'MARK_VISION_PROMPTED' });
    onClose();
  };

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 9, overflowY: 'auto' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">THE DREAM WALL · AGE {s.age}</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>
              PICTURE IT. BUILD IT.
            </h1>
          </div>
          <Button tone="primary" size="sm" onClick={done}>DONE</Button>
        </header>

        {/* the board */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <Eyebrow>YOUR DREAMS</Eyebrow>
            <Chip tone="brand">{board.length}/{MAX} · {achievedCount} DONE ✨</Chip>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
            {board.map((v) => {
              const p = v.condition ? conditionProgress(v.condition, s, c) : null;
              return (
                <motion.div
                  key={v.id}
                  layout
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  style={{
                    position: 'relative',
                    borderRadius: 16,
                    padding: 14,
                    textAlign: 'center',
                    background: v.achieved ? 'var(--up-soft)' : 'var(--surface-2)',
                    border: v.achieved ? '1.5px solid var(--up)' : '1.5px dashed var(--border-strong)',
                    boxShadow: v.achieved ? '0 0 0 1px var(--up), 0 8px 24px -10px var(--up)' : 'none',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => dispatch({ type: 'REMOVE_VISION', id: v.id })}
                    aria-label="Remove"
                    style={{ position: 'absolute', top: 6, right: 8, border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', fontSize: 15 }}
                  >
                    ×
                  </button>
                  <div
                    style={{
                      fontSize: 40,
                      filter: v.achieved ? 'none' : 'grayscale(1)',
                      opacity: v.achieved ? 1 : 0.55,
                      transition: 'filter 0.4s, opacity 0.4s',
                    }}
                  >
                    {v.icon}
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text)', marginTop: 4 }}>{v.title}</div>
                  <div style={{ fontSize: 11, color: v.achieved ? 'var(--up)' : 'var(--text-3)', marginTop: 2, fontWeight: 700 }}>
                    {v.achieved ? 'Achieved ✓' : v.condition ? conditionLabel(v.condition) : ''}
                  </div>
                  {!v.achieved && p && (
                    <div style={{ marginTop: 8, height: 6, borderRadius: 999, background: 'var(--surface-3)', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.round(p.frac * 100)}%`, height: '100%', background: 'var(--brand)', borderRadius: 999 }} />
                    </div>
                  )}
                </motion.div>
              );
            })}
            {/* empty slots */}
            {Array.from({ length: Math.max(0, MAX - board.length) }).map((_, i) => (
              <div
                key={`empty-${i}`}
                style={{
                  borderRadius: 16,
                  border: '1.5px dashed var(--border)',
                  minHeight: 130,
                  display: 'grid',
                  placeItems: 'center',
                  color: 'var(--text-3)',
                  fontSize: 24,
                }}
              >
                +
              </div>
            ))}
          </div>
          {board.length === 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--text-2)' }}>
              Pick the dreams that matter to you below — tap to pin them up.
            </p>
          )}
        </Card>

        {/* the gallery */}
        <Card>
          <Eyebrow color="var(--brand)">PIN A DREAM {full ? '· WALL FULL' : ''}</Eyebrow>
          <div style={{ marginTop: 12, display: 'grid', gap: 16 }}>
            {CATEGORIES.map((cat) => (
              <div key={cat.key}>
                <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-2)', marginBottom: 8 }}>
                  {cat.icon} {cat.label}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
                  {GALLERY.filter((g) => g.category === cat.key).map((g) => {
                    const added = onBoard(g.key);
                    const disabled = added || full;
                    return (
                      <motion.button
                        key={g.key}
                        type="button"
                        whileHover={disabled ? undefined : { y: -2 }}
                        whileTap={disabled ? undefined : { scale: 0.97 }}
                        onClick={() => add(g)}
                        disabled={disabled}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          textAlign: 'left',
                          padding: 10,
                          borderRadius: 12,
                          border: `1px solid ${added ? 'var(--up)' : 'var(--border)'}`,
                          background: added ? 'var(--up-soft)' : 'var(--surface-2)',
                          opacity: disabled && !added ? 0.5 : 1,
                          cursor: disabled ? 'default' : 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        <span style={{ fontSize: 22 }}>{g.icon}</span>
                        <span style={{ flex: 1 }}>
                          <span style={{ display: 'block', fontWeight: 800, fontSize: 13, color: 'var(--text)' }}>{g.title}</span>
                          <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-3)' }}>{conditionLabel(g.condition)}</span>
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 900, color: added ? 'var(--up)' : 'var(--brand)' }}>{added ? '✓' : '+'}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
